import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { GetHistoricalMonthlyReconciliationsByMatchSource } from "../../graphql/dashboard";
import { formatMoneyWithCurrency } from "../../utils/formatter";
import { useTranslation } from "react-i18next";
import { formatHmrPeriodLabel } from "../../utils/hmrPeriodLabel";
import ScfoButton from "../../components/shared/ScfoButton";

// NOTE: This used to be a Modal. Now it's a panel meant to be rendered inside HMRModal
// to avoid opening a modal over another modal.
const SKELETON_PERIODS = 3;
const SKELETON_MATCH_ROWS = 4;

const HMRBreakdownSkeleton = ({ label }) => (
  <div aria-busy="true" aria-live="polite">
    <span className="hmr-skeleton-visually-hidden">{label}</span>
    {Array.from({ length: SKELETON_PERIODS }, (_, periodIndex) => (
      <div
        key={periodIndex}
        className="hmr-modal-breakdown__period-block"
        aria-hidden="true"
      >
        <span className="hmr-skeleton-line hmr-skeleton-line--block-title" />
        <div className="hmr-modal-breakdown__table-head">
          <span className="hmr-skeleton-line hmr-skeleton-line--head" style={{ width: "22%" }} />
          <span className="hmr-skeleton-line hmr-skeleton-line--head" style={{ width: "14%", marginLeft: "auto" }} />
          <span className="hmr-skeleton-line hmr-skeleton-line--head" style={{ width: "18%", marginLeft: "8%" }} />
          <span className="hmr-skeleton-line hmr-skeleton-line--head" style={{ width: "10%", marginLeft: "6%" }} />
        </div>
        {Array.from({ length: SKELETON_MATCH_ROWS }, (_, rowIndex) => (
          <div key={rowIndex} className="hmr-modal-breakdown__row">
            <span className="hmr-skeleton-line hmr-skeleton-line--cell" style={{ width: "28%" }} />
            <span className="hmr-skeleton-line hmr-skeleton-line--cell" style={{ width: "12%", marginLeft: "auto" }} />
            <span className="hmr-skeleton-line hmr-skeleton-line--cell" style={{ width: "18%", marginLeft: "8%" }} />
            <span className="hmr-skeleton-line hmr-skeleton-line--cell" style={{ width: "8%", marginLeft: "6%" }} />
          </div>
        ))}
      </div>
    ))}
  </div>
);

export const HMRByEmailModal = ({ onBack }) => {
  const { t, i18n } = useTranslation("header");
  const { selectedCompany } = useSelector((state) => state.companyList);
  const query = GetHistoricalMonthlyReconciliationsByMatchSource({ companyId: selectedCompany.id });

  // Estado para rastrear qué tipos de match están expandidos: { "period-matchSource": true }
  const [expandedMatches, setExpandedMatches] = useState({});
  const content = useMemo(
    () => query.data?.getHistoricalMonthlyReconciliationsByMatchSource || [],
    [query.data]
  );
  const showSkeleton = !query.error && !Array.isArray(query.data?.getHistoricalMonthlyReconciliationsByMatchSource);

  useEffect(() => {
    query.reExecuteQuery({ requestPolicy: "network-only" });
  }, [selectedCompany.id]);

  const toggleMatchExpansion = (period, matchSource) => {
    const key = `${period}-${matchSource}`;
    setExpandedMatches((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="hmr-modal-breakdown">
      <div className="hmr-modal-breakdown__toolbar">
        <h3 className="hmr-modal-breakdown__title">{t("reconciliation.matchBreakdown")}</h3>
        <ScfoButton type="button" variant="ghost" size="sm" onClick={onBack}>
          {t("reconciliation.back")}
        </ScfoButton>
      </div>

      <div className="hmr-modal-breakdown__scroll">
        {showSkeleton ? (
          <HMRBreakdownSkeleton label={t("reconciliation.loading")} />
        ) : content?.map((row) => {
          const label = formatHmrPeriodLabel(row.period, i18n.language) || "-";
          const total = row.totalReconciledAmount || 0;

          return (
            <div key={row.period} className="hmr-modal-breakdown__period-block">
              <div className="hmr-modal-breakdown__period-title">
                {label}
              </div>

              <div>
                {(row.matchSources || []).length === 0 ? (
                  <div className="text-muted">{t("reconciliation.noValidMatches")}</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    <div className="hmr-modal-breakdown__table-head">
                      <div style={{ width: "35%", paddingLeft: "12px" }}>{t("reconciliation.matchType")}</div>
                      <div style={{ width: "20%", textAlign: "center" }}>{t("reconciliation.matches")}</div>
                      <div style={{ width: "30%", textAlign: "center" }}>{t("reconciliation.reconciledAmount")}</div>
                      <div style={{ width: "15%", textAlign: "right", paddingRight: "8px" }}>{t("reconciliation.matchesPercent")}</div>
                    </div>
                    {(row.matchSources || []).map((s, index) => {
                      const matchKey = `${row.period}-${s.matchSource}`;
                      const isExpanded = expandedMatches[matchKey];
                      const userEmails = s.userEmails || [];
                      const hasEmails = userEmails.length > 0;
                      const isLastRow = index === (row.matchSources || []).length - 1;

                      return (
                        <div key={matchKey} className="d-flex flex-column">
                          <div
                            className="hmr-modal-breakdown__row"
                            style={{
                              cursor: hasEmails ? "pointer" : "default",
                              borderBottom: isLastRow ? "none" : "1px solid var(--border)",
                            }}
                            onClick={() => hasEmails && toggleMatchExpansion(row.period, s.matchSource)}
                          >
                            <div style={{ width: "35%", paddingLeft: "12px" }}>
                              <div className="d-flex align-items-center gap-2">
                                <div className="fw-semibold" style={{ fontSize: "11pt" }}>{s.matchSource || "-"}</div>
                                {hasEmails && (
                                  <span className="text-muted" style={{ fontSize: "9pt" }}>
                                    {isExpanded ? "▼" : "▶"}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div style={{ width: "20%", textAlign: "center", fontSize: "11pt" }}>
                              {s.matchesCount || 0}
                            </div>
                            <div style={{ width: "30%", textAlign: "center", fontSize: "11pt" }}>
                              {formatMoneyWithCurrency(s.reconciledAmount || 0, "CLP")}
                            </div>
                            <div style={{ width: "15%", textAlign: "right", paddingRight: "8px", fontSize: "11pt", fontWeight: "600" }}>
                              {parseFloat(s.percentage || 0).toFixed(1)}%
                            </div>
                          </div>
                          {isExpanded && hasEmails && (
                            <div className="d-flex flex-column">
                              {userEmails.map((userEmail, idx) => {
                                const isLastUserEmail = idx === userEmails.length - 1;
                                const isLastMatchSource = isLastRow;
                                return (
                                <div 
                                  key={idx} 
                                  className="d-flex align-items-center py-1"
                                  style={{ 
                                    fontSize: "10pt",
                                    borderBottom: (isLastUserEmail && isLastMatchSource) ? "none" : (idx < userEmails.length - 1 ? "1px solid var(--border)" : "none"),
                                  }}
                                >
                                  <div style={{ width: "35%", paddingLeft: "12px" }}>
                                    <div className="text-muted" style={{ paddingLeft: "24px" }}>
                                      • {userEmail.email}
                                    </div>
                                  </div>
                                  <div style={{ width: "20%", textAlign: "center", fontSize: "10pt" }}>
                                    {userEmail.matchesCount}
                                  </div>
                                  <div style={{ width: "30%", textAlign: "center" }}>
                                    {/* Vacío para mantener alineación */}
                                  </div>
                                  <div style={{ width: "15%", textAlign: "right", paddingRight: "8px" }}>
                                    {/* Vacío para mantener alineación */}
                                  </div>
                                </div>
                              )})}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {/* Fila de totales */}
                    <div className="hmr-modal-breakdown__totals">
                      <div style={{ width: "35%", paddingLeft: "12px" }}>
                        {t("reconciliation.total")}
                      </div>
                      <div style={{ width: "20%", textAlign: "center" }}>
                        {row.totalMatchesCount || 0}
                      </div>
                      <div style={{ width: "30%", textAlign: "center" }}>
                        {formatMoneyWithCurrency(total, "CLP")}
                      </div>
                      <div style={{ width: "15%", textAlign: "right", paddingRight: "8px" }}>
                        100.0%
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

